import type { ReactNode } from 'react';
import { createContext, type JSX, use } from 'react';
import type { Callback } from 'ts/base/Callback';
import { ArrayUtils } from 'ts/commons/ArrayUtils';
import { Assertions } from 'ts/commons/Assertions';
import type { FindingsSummaryCategoryInfo } from 'typedefs/FindingsSummaryCategoryInfo';
import type { FindingsSummaryGroupInfo } from 'typedefs/FindingsSummaryGroupInfo';
import type { FindingsSummaryInfo } from 'typedefs/FindingsSummaryInfo';
import type { FindingsSummaryTypeIdInfo } from 'typedefs/FindingsSummaryTypeIdInfo';

/** The "public" API of the filter options. */
export type FindingCategoryFilter = {
	/** List of filters. Every string is either a category, category/group, or type id. */
	categoryGroupTypeFilters: string[];

	/** If true, the category/group/type filters are inverted. */
	invertCategoryGroupTypeFilters: boolean;
};

/** Exposed filter options. */
export type ExposedFindingCategoryFilterContext = EnhancedFindingCategorySelectionState & {
	selectedFindingTypeIds: string[];
	selectedGroupNames: string[];
	selectedCategoryNames: string[];
	toggleAllFindingCategories: (inclusionType: InclusionType) => void;
	toggleFindingCategory: (categoryInfo: FindingsSummaryCategoryInfo, inclusionType: InclusionType) => void;
	toggleFindingGroup: (findingGroupInfo: FindingsSummaryGroupInfo, inclusionType: InclusionType) => void;
	toggleFindingType: (findingType: FindingsSummaryTypeIdInfo, inclusionType: InclusionType) => void;
	findingCategoryFilter: FindingCategoryFilter;
};

type EnhancedFindingCategorySelectionState = {
	/** The current selection of categories. */
	selectedCategories: FindingsSummaryCategoryInfo[];
	allCategoriesSelected: boolean;
	totalFindingCount: number;
	categories: FindingsSummaryCategoryInfo[];
	/** The groups extracted from {@link categories}. */
	groups: FindingsSummaryGroupInfo[];
	/** The current selection of groups. */
	selectedGroups: FindingsSummaryGroupInfo[];
	groupIsPartlySelected: (group: FindingsSummaryGroupInfo) => boolean;
	/** The finding types extracted from {@link categories}. */
	findingTypes: FindingsSummaryTypeIdInfo[];
	/** The current selection of finding types. */
	selectedFindingTypes: FindingsSummaryTypeIdInfo[];
	emptyGroups: FindingsSummaryGroupInfo[];
	/** The current selection of groups which contain no findings (relevant if reportCategoriesWithoutFindings is true). */
	selectedEmptyGroups: FindingsSummaryGroupInfo[];

	getQualifiedGroupNameFromGroup: (group: string) => string;
	nonEmptyGroups: FindingsSummaryGroupInfo[];
};

/** Context that holds the current state of the test filter. */
const FindingCategoryFilterContext = createContext<ExposedFindingCategoryFilterContext | undefined>(undefined);

/** Props for FindingFilterContextProvider. */
type FindingCategoryFilterContextProviderProps = {
	findingSummary: FindingsSummaryInfo;
	findingsCategoryFilter: FindingCategoryFilter;
	onChange: Callback<FindingCategoryFilter>;
	children: ReactNode;
};

/** Context provider for the finding category filter. */
export function FindingCategoryFilterContextProvider({
	findingSummary,
	findingsCategoryFilter,
	onChange,
	children
}: FindingCategoryFilterContextProviderProps): JSX.Element {
	const findingCategoryContext = getCategoryFilterContext(findingSummary, findingsCategoryFilter, onChange);

	return <FindingCategoryFilterContext value={findingCategoryContext}>{children}</FindingCategoryFilterContext>;
}

/**
 * Returns the current finding category filter settings and exposes some functions to update the filter. Must only be
 * used in components nested within a FindingCategoryFilterContextProvider.
 */
export function useFindingCategoryFilterContext(): ExposedFindingCategoryFilterContext {
	const context = useFindingCategoryFilterContextIfAvailable();
	if (context === undefined) {
		throw new Error('useFindingCategoryFilterContext must be used within a FindingCategoryFilterContextProvider');
	}
	return context;
}

/** Returns the current finding category filter settings and exposes some functions to update the filter. */
function useFindingCategoryFilterContextIfAvailable(): ExposedFindingCategoryFilterContext | undefined {
	return use(FindingCategoryFilterContext);
}

function deriveEnhancedFindingCategorySelectionState(
	findingSummary: FindingsSummaryInfo,
	findingCategoryFilter: FindingCategoryFilter
): EnhancedFindingCategorySelectionState {
	const categories = findingSummary.categoryInfos;
	const groups: FindingsSummaryGroupInfo[] = [];
	const findingTypes: FindingsSummaryTypeIdInfo[] = [];

	/** Maps from finding type ids to group names. */
	const findingTypeToGroup: Record<string, string> = {};
	/** Maps from group names to category names. */
	const groupToCategory: Record<string, string> = {};
	for (const category of categories) {
		for (const group of category.groupInfos) {
			groups.push(group);
			groupToCategory[group.groupName] = category.categoryName;
			for (const findingType of group.typeIdInfos) {
				findingTypes.push(findingType);
				findingTypeToGroup[findingType.typeIdName] = group.groupName;
			}
		}
	}

	function getQualifiedGroupNameFromGroup(group: string): string {
		return `${groupToCategory[group]!}/${group}`;
	}

	function getQualifiedGroupNameFromFinding(findingType: FindingsSummaryTypeIdInfo): string {
		return getQualifiedGroupNameFromGroup(findingTypeToGroup[findingType.typeIdName]!);
	}

	function getCategoryNameFromFinding(findingType: FindingsSummaryTypeIdInfo): string {
		return groupToCategory[findingTypeToGroup[findingType.typeIdName]!]!;
	}

	const categoryGroupTypeFilters = findingCategoryFilter.categoryGroupTypeFilters;
	/** The current selection of finding types. */
	let selectedFindingTypes: FindingsSummaryTypeIdInfo[] = findingTypes.filter(
		findingType =>
			categoryGroupTypeFilters.includes(findingType.typeIdName) ||
			categoryGroupTypeFilters.includes(getQualifiedGroupNameFromFinding(findingType)) ||
			categoryGroupTypeFilters.includes(getCategoryNameFromFinding(findingType))
	);
	const emptyGroups: FindingsSummaryGroupInfo[] = groups.filter(group => !group.typeIdInfos.length);
	let selectedEmptyGroups: FindingsSummaryGroupInfo[] = emptyGroups.filter(
		group =>
			categoryGroupTypeFilters.includes(getQualifiedGroupNameFromGroup(group.groupName)) ||
			categoryGroupTypeFilters.includes(groupToCategory[group.groupName]!)
	);

	if (!findingCategoryFilter.invertCategoryGroupTypeFilters) {
		// Note that an inverted filter means that the categories/groups/types should be *included*
		selectedFindingTypes = ArrayUtils.differenceSet(findingTypes, selectedFindingTypes);
		selectedEmptyGroups = ArrayUtils.differenceSet(emptyGroups, selectedEmptyGroups);
	}

	const allCategoriesSelected =
		selectedFindingTypes.length === findingTypes.length && selectedEmptyGroups.length === emptyGroups.length;
	const totalFindingCount = findingSummary.categoryInfos
		.map(summary => summary.count)
		.reduce((sum, current) => sum + current, 0);
	const nonEmptyGroups = groups.filter(group => group.typeIdInfos.length);
	const selectedGroups: FindingsSummaryGroupInfo[] = nonEmptyGroups
		.filter(group => ArrayUtils.arrayIsSubset(group.typeIdInfos, selectedFindingTypes))
		.concat(selectedEmptyGroups);
	const selectedCategories: FindingsSummaryCategoryInfo[] = categories.filter(category =>
		ArrayUtils.arrayIsSubset(category.groupInfos, selectedGroups)
	);

	const groupIsPartlySelected = (group: FindingsSummaryGroupInfo): boolean => {
		const numSelectedFromGroup = ArrayUtils.intersection(group.typeIdInfos, selectedFindingTypes).length;
		return numSelectedFromGroup > 0 && numSelectedFromGroup < group.typeIdInfos.length;
	};

	return {
		categories,
		groups,
		findingTypes,
		selectedFindingTypes,
		selectedEmptyGroups,
		emptyGroups,
		getQualifiedGroupNameFromGroup,
		allCategoriesSelected,
		totalFindingCount,
		selectedCategories,
		selectedGroups,
		groupIsPartlySelected,
		nonEmptyGroups
	};
}

/**
 * Determines whether the filter query should be inverted (i.e., if the _included_ or the _excluded_ finding groups
 * should be passed to the service). The default, non-inverted query passes the _excluded_ findings.
 */
function shouldInvertQuery(selectionState: EnhancedFindingCategorySelectionState): boolean {
	return selectionState.selectedFindingTypes.length / selectionState.findingTypes.length < 0.5;
}

/** Returns the empty groups that should be used for the filter, taking into account query inversion. */
function getEmptyGroupsForFilter(selectionState: EnhancedFindingCategorySelectionState): FindingsSummaryGroupInfo[] {
	if (shouldInvertQuery(selectionState)) {
		return [...selectionState.selectedEmptyGroups];
	} else {
		return ArrayUtils.differenceSet(selectionState.emptyGroups, selectionState.selectedEmptyGroups);
	}
}

/** Returns the finding types that should be used for the filter, taking into account query inversion. */
function getFindingTypesForFilter(selectionState: EnhancedFindingCategorySelectionState): FindingsSummaryTypeIdInfo[] {
	if (shouldInvertQuery(selectionState)) {
		return [...selectionState.selectedFindingTypes];
	} else {
		return ArrayUtils.differenceSet(selectionState.findingTypes, selectionState.selectedFindingTypes);
	}
}

/** Inclusion type of the finding category/group/type filter. */
export enum InclusionType {
	ADD_INCLUDE,
	ADD_EXCLUDE,
	INCLUDE_ONLY
}

/**
 * Handles updates to the current selection (caused by checkbox clicks).
 *
 * @param updatedEmptyGroups The empty groups that have been updated. Similar to {@code updatedFindingTypeIds}, we have
 *   to use the group names instead of objects of type {@link FindingsSummaryGroupInfo}.
 * @param updatedFindingTypes The finding types that have been updated. We have to use the ids here instead of
 *   {@link FindingsSummaryTypeIdInfo}, because the actual {@link findingTypes} can change when the component is
 *   re-initialized (see {@link initialize}).
 * @param selected Whether the updated finding types and empty groups have been selected or de-selected.
 */
function getCategoryFilterForToggledFindingTypeSelection(
	selectionState: EnhancedFindingCategorySelectionState,
	updatedEmptyGroups: FindingsSummaryGroupInfo[],
	updatedFindingTypes: FindingsSummaryTypeIdInfo[],
	inclusionType: InclusionType
): FindingCategoryFilter {
	Assertions.assert(
		updatedEmptyGroups.every(group => !group.typeIdInfos.length),
		'All updated groups must be empty'
	);

	switch (inclusionType) {
		case InclusionType.INCLUDE_ONLY:
			selectionState.selectedFindingTypes = ArrayUtils.removeDuplicates(updatedFindingTypes);
			selectionState.selectedEmptyGroups = ArrayUtils.removeDuplicates(updatedEmptyGroups);
			break;
		case InclusionType.ADD_INCLUDE:
			ArrayUtils.addAllToArray(selectionState.selectedFindingTypes, updatedFindingTypes);
			selectionState.selectedFindingTypes = ArrayUtils.removeDuplicates(selectionState.selectedFindingTypes);
			ArrayUtils.addAllToArray(selectionState.selectedEmptyGroups, updatedEmptyGroups);
			selectionState.selectedEmptyGroups = ArrayUtils.removeDuplicates(selectionState.selectedEmptyGroups);
			break;
		case InclusionType.ADD_EXCLUDE:
			ArrayUtils.removeAll(selectionState.selectedFindingTypes, updatedFindingTypes);
			ArrayUtils.removeAll(selectionState.selectedEmptyGroups, updatedEmptyGroups);
	}
	const filterFindingTypes = getFindingTypesForFilter(selectionState);
	const filterGroups = selectionState.nonEmptyGroups
		.filter(group => group.typeIdInfos.every(findingType => filterFindingTypes.includes(findingType)))
		.concat(getEmptyGroupsForFilter(selectionState));
	filterGroups.forEach(group => ArrayUtils.removeAll(filterFindingTypes, group.typeIdInfos));
	const filterCategories = selectionState.categories.filter(category =>
		category.groupInfos.every(group => filterGroups.includes(group))
	);
	filterCategories.forEach(category => ArrayUtils.removeAll(filterGroups, category.groupInfos));

	const filter = filterCategories
		.map(c => c.categoryName)
		.concat(filterGroups.map(g => selectionState.getQualifiedGroupNameFromGroup(g.groupName)))
		.concat(filterFindingTypes.map(findingType => findingType.typeIdName));

	return {
		categoryGroupTypeFilters: filter,
		invertCategoryGroupTypeFilters: shouldInvertQuery(selectionState)
	};
}

/**
 * Builds the category filter context, which exposes methods to toggle finding categories, groups and individual type
 * IDs. Any updates to the underlying data are reflected by calling the given callback.
 */
export function getCategoryFilterContext(
	findingSummary: FindingsSummaryInfo,
	findingCategoryFilter: FindingCategoryFilter,
	onFindingCategoryFilterChange: Callback<FindingCategoryFilter>
): ExposedFindingCategoryFilterContext {
	const selectionState = deriveEnhancedFindingCategorySelectionState(findingSummary, findingCategoryFilter);

	function toggleFindingTypeSelection(
		updatedFindingTypes: FindingsSummaryTypeIdInfo[],
		updatedEmptyGroups: FindingsSummaryGroupInfo[],
		inclusionType: InclusionType
	) {
		onFindingCategoryFilterChange(
			getCategoryFilterForToggledFindingTypeSelection(
				selectionState,
				updatedEmptyGroups,
				updatedFindingTypes,
				inclusionType
			)
		);
	}

	const selectedFindingTypeIds = selectionState.selectedFindingTypes.map(type => type.typeIdName);
	const selectedGroupNames = selectionState.selectedGroups.map(group => group.groupName);
	const selectedCategoryNames = selectionState.selectedCategories.map(category => category.categoryName);

	return {
		...selectionState,
		findingCategoryFilter,
		selectedFindingTypeIds,
		selectedGroupNames,
		selectedCategoryNames,
		toggleAllFindingCategories(inclusionType: InclusionType) {
			toggleFindingTypeSelection(selectionState.findingTypes, selectionState.emptyGroups, inclusionType);
		},
		toggleFindingCategory(categoryInfo, inclusionType) {
			const typeIds = categoryInfo.groupInfos.flatMap(group => group.typeIdInfos);
			const emptyGroups = categoryInfo.groupInfos.filter(group => !group.typeIdInfos.length);
			toggleFindingTypeSelection(typeIds, emptyGroups, inclusionType);
		},
		toggleFindingGroup(findingGroupInfo, inclusionType) {
			if (findingGroupInfo.count === 0) {
				toggleFindingTypeSelection([], [findingGroupInfo], inclusionType);
			} else {
				toggleFindingTypeSelection(findingGroupInfo.typeIdInfos, [], inclusionType);
			}
		},
		toggleFindingType(typeIdInfo, inclusionType) {
			toggleFindingTypeSelection([typeIdInfo], [], inclusionType);
		}
	};
}
